import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Order } from "types";

type OrderStateReasonDialogMode = "stop" | "cancel";

type Props = {
  open: boolean;
  mode: OrderStateReasonDialogMode;
  order: Order | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
};

export default function OrderStateReasonDialog({
  open,
  mode,
  order,
  loading = false,
  onClose,
  onSubmit,
}: Props) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      return;
    }

    if (mode === "stop") {
      setReason(order?.movement_order?.stopped_reason ?? "");
      return;
    }

    setReason("");
  }, [mode, open, order]);

  const isCancelMode = mode === "cancel";
  const normalizedReason = reason.trim();

  const title = useMemo(
    () => (isCancelMode ? "Cancelar pedido" : "Detener pedido"),
    [isCancelMode],
  );

  const helperText = useMemo(() => {
    if (isCancelMode) {
      return "La cancelación requiere un motivo.";
    }

    return "Podés indicar un motivo para dejar trazabilidad. Es opcional.";
  }, [isCancelMode]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!loading) {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} pt={0.5}>
          <Typography variant="body2">
            <strong>Pedido:</strong> {order?.id ?? "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Remito:</strong> {order?.numero_remito ?? "-"}
          </Typography>
          <TextField
            label="Motivo"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
            required={isCancelMode}
            helperText={helperText}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={loading || (isCancelMode && !normalizedReason)}
          onClick={() => {
            void onSubmit(normalizedReason);
          }}
        >
          {loading
            ? isCancelMode
              ? "Cancelando..."
              : "Deteniendo..."
            : isCancelMode
              ? "Confirmar cancelación"
              : "Confirmar detención"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
